import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useGetLeadSubscriptionStatus } from "lib/hooks/api/lead_subscriptions/useGetLeadSubscriptionStatus";
import { useLeadSubscriptionUnsubscribe } from "lib/hooks/api/lead_subscriptions/useLeadSubscriptionUnsubscribe";
import { routes } from "lib/routes";

import StotlesTada from "../../../assets/images/stotles_tada.svg";
import UnsubscribeAeroplane from "../../../assets/images/unsubscribe_aeroplane.svg";

type Props = {
  /** Read from the `?token=` search param by the TanStack route. */
  token: string | null;
};

export function UnsubscribePage({ token }: Props): JSX.Element {
  const subscriptionStatusRequest = useGetLeadSubscriptionStatus(token);
  const { mutate: unsubscribe, isLoading: unsubscribing } = useLeadSubscriptionUnsubscribe();
  if (!token) {
    return (
      <PageContent>
        <EmptyState size="large" type="loadError" heading="Invalid unsubscribe token" />
      </PageContent>
    );
  }

  if (subscriptionStatusRequest.isLoading) {
    return <CentredSpinner />;
  }

  if (subscriptionStatusRequest.isError) {
    return (
      <PageContent>
        <EmptyState size="large" type="loadError" heading="Something went wrong" />
      </PageContent>
    );
  }

  if (subscriptionStatusRequest.data?.status === "INACTIVE") {
    return (
      <PageContent>
        <Container>
          <Content>
            <Text h1>You've been successfully unsubscribed</Text>
            <Text body color="sysTextSecondary">
              If you ever change your mind, feel free to create alerts in app at any time. In the
              meantime, there are public sector contracts with your name on them, so why not check
              them out on Stotles?
            </Text>
            <div>
              <Button as="a" to={routes.notices}>
                Find opportunities
              </Button>
            </div>
          </Content>
          <div>
            <img src={StotlesTada} alt="Success" />
          </div>
        </Container>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Container>
        <Content>
          <Text h1>Unsubscribe</Text>
          <Text body color="sysTextSecondary">
            We're sorry to see you leave, but we understand that everyone's inbox is personal and
            respect your decision to unsubscribe. To complete the unsubscribe process, click the
            button below.
          </Text>
          <div>
            <Button
              onClick={() => unsubscribe({ input: { token, listUnsubscribe: "Standard" } })}
              loading={unsubscribing}
            >
              Unsubscribe
            </Button>
          </div>
          <Text body color="sysTextDefault">
            If there's anything we can do to make your experience better, please let us know by
            emailing <a href="mailto:team@stotles.com">team@stotles.com</a>
          </Text>
        </Content>
        <img src={UnsubscribeAeroplane} alt="Unsubscribe" />
      </Container>
    </PageContent>
  );
}

const PageContent = styled.div({
  maxWidth: 1000,
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  padding: "120px 20px",
});

const Container = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  columnGap: 125,
  rowGap: 50,
  alignItems: "center",
  "& img": {
    width: "100%",
  },
});

const Content = styled.div({
  display: "grid",
  gap: 20,
});
